<template>
    <div class="row">
        <div class="col-sm-5 pb-3">
            <div class="row">
                <div class="col-sm-12">
                    <label>
                        Name <span class="color-6">*</span>
                    </label>

                    <div class="d-flex mb-2">
                        <input type="text" class="full" :class="{'border-color-6': $v.reservation.customer.name.$invalid && !$v.reservation.customer.name.required}" v-model="reservation.customer.name">
                    </div>
                </div>

                <div class="col-sm-6">
                    <label>
                        Country <span class="color-6">*</span>
                    </label>

                    <country-picker :topCodes="topCountries" :selectedCountryValue="selectedCountry" :disabled="false" :allowEmpty="false" @input="updateCountry($event, 'country')" :customClass="'mb-2'" :error="!selectedCountry" ></country-picker>
                </div>

                <div class="col-sm-6">
                    <label>Phone Number</label>

                    <div class="mb-2">
                        <PhoneInput
                            ref="phoneInput"
                            v-model="reservation.customer.phone"
                            :country="phoneCountry"
                            @validate="isPhoneValid"
                            @country-changed="onFlagChanged"
                            @input="onPhoneInput"
                            class="w-100"
                        />
                        <div v-if="reservation.customer.phone && !customerPhoneValid" class="error">
                            <span>Phone Number Invalid.</span>
                        </div>
                    </div>
                </div>

                <div class="col-sm-12">
                    <label>
                        Email
                        <span v-if="$v.reservation.customer.email.$invalid" class="color-6">
                            <span v-if="!$v.reservation.customer.email.email">Invalid</span>
                        </span>
                    </label>

                    <input type="email" class="full mb-2" v-model="reservation.customer.email">
                </div>

                <div class="col-sm-6">
                    <label>Hotel</label>

                    <input type="text" class="full" v-model="reservation.customer.accomodation">
                </div>

                <div class="col-sm-6">
                    <label>Room</label>

                    <input type="text" class="full" v-model="reservation.customer.room">
                </div>

                <div class="col-sm-12 d-flex align-items-center mt-3">
                    <checkbox class="mr-2" v-model="reservation.customer.sendEmailCopy"></checkbox>
                    
                    <label>Send a confirmation email to the customer</label>
                </div>
            </div>
        </div>

        <div class="col-sm-7 pb-3 flex-column">
            <div class="row h-100">

               <div
                    v-if="reservation.event.bookingQuestionIsActive && reservation.event.bookingQuestion"
                    class="col-sm-12 d-flex flex-column mb-3"
                >
                    <label>
                        {{ reservation.event.bookingQuestion }}  <span v-if="reservation.event.bookingQuestionIsMandatory" class="color-6">*</span>
                    </label>

                    <input
                        type="text"
                        v-model="reservation.bookingAnswer"
                        :class="{'border-color-6': $v.reservation.bookingAnswer.$invalid && reservation.event.bookingQuestionIsMandatory}"
                        class="full p-2"
                    />
                </div>
                <div v-if="canSeeInternalNote" class="col-sm-12 d-flex flex-column mb-3">
                    <label>
                        Internal notes:<span class="fs-smaller normal ml-1">(internal use only)</span>
                    </label>
                    <textarea
                        :disabled="!canEditInternalNote"
                        v-model="reservation.internalNote"
                        class="flex-grow-1 p-1 fs-bigger bg-lighter darker bold"
                        rows="1">
                    </textarea>
                </div>

                <!-- Public note -->
                <div class="col-sm-12 d-flex flex-column">

                    <label>
                        Notes:<span class="fs-smaller normal ml-1">(agents can see these)</span>
                    </label>

                    <textarea
                        v-model="reservation.note"
                        class="flex-grow-1 p-1 fs-bigger bg-lighter darker bold"
                        rows="1">
                    </textarea>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import { requiredIf, required, email, minLength } from 'vuelidate/lib/validators'
    import { TOP_COUNTRY_CODES } from '@/enums/topCountries';
    import extendedVSelect from '@/extends/vue-select'
    import CountryPicker from '@/tools/CountryPicker'
    import PhoneInput from '@/components/common/PhoneInput.vue'
    import phoneCountrySync from '@/mixins/phoneCountrySync';

    export default {
        mixins: [phoneCountrySync],
        data() {
            return {
                selectedCountry: null,
                topCountries: TOP_COUNTRY_CODES,
                customerPhoneValid: true,
            }
        },

        props: {
            reservation: {
                type: Object
            },
            readOnly: {
                type: Boolean,
                default: false
            }
        },
        validations: {
            reservation:{
                customer: {
                    name: {
                        required
                    },
                    country: {
                        required
                    },
                    email: {
                        email
                    }
                },
                bookingAnswer: {
                    required: requiredIf(function () {
                        return this.reservation.event.bookingQuestionIsMandatory
                    })
                }
            }
        },
        created() {
            this.setDefinedCountry();
            if(this.reservation.event.bookingQuestion){
                this.$set(this.getReservation, 'bookingQuestion', this.reservation.event.bookingQuestion)
            }
        },
        computed: {
            ...mapGetters([
                'getCountries',
                'getNamePrefixList',
                'getCanReadInternalNote',
                'getCanEditInternalNote',
                'getReservation',
            ]),
            canSeeInternalNote() {
                return this.getCanReadInternalNote;
            },
            canEditInternalNote() {
                return this.getCanEditInternalNote;
            }
        },
        methods: {
            setDefinedCountry() {
                const defaultCountry = this.getCountries.find(c => c.default) || null;
                this.selectedCountry = this.getCountries.find(c => c.id == this.reservation.customer.country) || defaultCountry;
                this.phoneCountry = this.selectedCountry ? this.selectedCountry.code2 : null;

                if (this.selectedCountry) {
                    this.applyCountryInfo(this.selectedCountry);
                }
            },
            isPhoneValid(phoneObj) {
                const valid = !!phoneObj.valid;
                this.customerPhoneValid = valid;

                if (this.reservation.customer) {
                    this.reservation.customer.isInvalid = !valid;

                    const dial = phoneObj.countryCallingCode ? `+${String(phoneObj.countryCallingCode)}` : null;
                    this.$set(this.reservation.customer, 'dial', dial);
                }
            },
            onFlagChanged(country) {
                if (!this.isPhoneInputInitialized) return;

                const dial = country.dial;
                if (this.reservation.customer) {
                    this.$set(this.reservation.customer, 'dial', dial);
                }

                if (dial && this.reservation.customer.phone && !this.reservation.customer.phone.startsWith(dial)) {
                    this.reservation.customer.phone = dial;
                }

                if (this.isChangingCountryFromPicker) {
                    this.isChangingCountryFromPicker = false;
                    return;
                }

                if (country.iso2) {
                    const matchedCountry = this.getCountries.find(c => c.code2 === country.iso2);
                    if (matchedCountry && (!this.selectedCountry || this.selectedCountry.id !== matchedCountry.id)) {
                        this.selectedCountry = matchedCountry;
                        this.applyCountryInfo(matchedCountry);
                    }
                }
            },
            updateCountry(event = null, type) {
                this.selectedCountry = event;

                if (type === 'country') {
                    this.applyCountryInfo(this.selectedCountry);
                    this.isChangingCountryFromPicker = true;
                    this.reservation.customer.phone = '';
                    this.phoneCountry = this.selectedCountry.code2;
                    this.$refs.phoneInput.$refs.telInput.choose(this.selectedCountry.code2);
                }
            },
            applyCountryInfo(country) {
                this.reservation.customer.country = country.id;
                this.reservation.customer.dial = country.code;
                this.reservation.customer.countryName = country.name;
            },
            resetCountryInfo() {
                this.reservation.customer.country = null;
                this.reservation.customer.dial = null;
                this.reservation.customer.countryName = '';
                this.selectedCountry = null;
            }
        },
        components: {
            vSelect: extendedVSelect,
            CountryPicker,
            PhoneInput
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../../assets/scss/_variables.scss";
    
    input, textarea {
        background-color: $lighter;
    }
</style>


// WEBPACK FOOTER //
// CustomerDetails.vue?4d1dad46